import {
    IQueryContext,
    IQueryContextDatasourceColumn,
} from 'src/app/services/query-context/models/IQueryContext';
import { ISegmentDatasourceColumnState } from 'src/app/dashboards/common/items/filter/segment-filter/segments';
import { DatePickerSqlTokensCompiler } from 'src/app/utils/datasource-sql-map/date-picker-sql-tokens-compiler';
import {
    ICompiledSQLTokens,
    IDatasourceSQLMap,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import {
    multipleSelectComponentCompileSQLTokens,
    segmentContainerIdCompileSQLTokens,
} from 'src/app/utils/datasource-sql-map/utils';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { buildSplitGroupSQL, buildWhereSQL } from 'src/app/utils/query/sql';
import { sqlDateTrunc } from 'src/app/utils/sql';
import { BQSqlFunctionHelper } from 'src/app/utils/datasource-sql-map/bq-sql-function-helper';

export class BiVizDashboardEngagement {
    public static sqlMap: IDatasourceSQLMap = {
        dataset: 'bi_viz',
        tableId: 'dashboard_engagement',
        columns: {
            ACTIVITY_DATE: {
                columnId: 'ACTIVITY_DATE',
                columnSQL: 'start_date',
                compileSQLTokens: (state) => {
                    return DatePickerSqlTokensCompiler.compileTokens(
                        'start_date',
                        state
                    );
                },
            },
            ACTIVITY_APP_VERSION: {
                columnId: 'ACTIVITY_APP_VERSION',
                columnSQL: 'app_version',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'app_version',
                        state
                    );
                },
            },
            ACTIVITY_APP_VERSION_GROUP_MINOR: {
                columnId: 'ACTIVITY_APP_VERSION_GROUP_MINOR',
                columnSQL: `${BQSqlFunctionHelper.getCallSqlStatement(
                    BQSqlFunctionHelper.VERSION_HIERARCHY,
                    ['app_version']
                )}.minor`,
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        `${BQSqlFunctionHelper.getCallSqlStatement(
                            BQSqlFunctionHelper.VERSION_HIERARCHY,
                            ['app_version']
                        )}.minor`,
                        state
                    );
                },
            },
            FIRST_SESSION_DATE: {
                columnId: 'FIRST_SESSION_DATE',
                columnSQL: 'user_dim.first_session_date',
                compileSQLTokens: (state) => {
                    return DatePickerSqlTokensCompiler.compileTokens(
                        'user_dim.first_session_date',
                        state
                    );
                },
            },
            FIRST_GEO_COUNTRY_TIER: {
                columnId: 'FIRST_GEO_COUNTRY_TIER',
                columnSQL: 'user_dim.first_geo.country_tier_label',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'user_dim.first_geo.country_tier_label',
                        state
                    );
                },
            },
            FIRST_GEO_COUNTRY: {
                columnId: 'FIRST_GEO_COUNTRY',
                columnSQL: 'user_dim.first_geo.unified_country',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'user_dim.first_geo.unified_country',
                        state
                    );
                },
            },
            FIRST_ATTRIBUTION_UNIFIED_TRAFFIC: {
                columnId: 'FIRST_ATTRIBUTION_UNIFIED_TRAFFIC',
                columnSQL: 'user_dim.first_attribution.unified_traffic',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'user_dim.first_attribution.unified_traffic',
                        state
                    );
                },
            },
            FIRST_APP_VERSION: {
                columnId: 'FIRST_APP_VERSION',
                columnSQL: 'user_dim.first_app.version',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'user_dim.first_app.version',
                        state
                    );
                },
            },
            FIRST_APP_VERSION_GROUP_MINOR: {
                columnId: 'FIRST_APP_VERSION_GROUP_MINOR',
                columnSQL: `${BQSqlFunctionHelper.getCallSqlStatement(
                    BQSqlFunctionHelper.VERSION_HIERARCHY,
                    ['user_dim.first_app.version']
                )}.minor`,
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        `${BQSqlFunctionHelper.getCallSqlStatement(
                            BQSqlFunctionHelper.VERSION_HIERARCHY,
                            ['user_dim.first_app.version']
                        )}.minor`,
                        state
                    );
                },
            },
            REVENUE_PAYER: {
                columnId: 'REVENUE_PAYER',
                columnSQL: 'user_dim.revenue.badge',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'user_dim.revenue.badge',
                        state
                    );
                },
            },
            REVENUE_BADGE: {
                columnId: 'REVENUE_BADGE',
                columnSQL: 'user_dim.revenue.badge',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'user_dim.revenue.badge',
                        state
                    );
                },
            },
            FIRST_ATTRIBUTION_UNIFIED_NETWORK: {
                columnId: 'FIRST_ATTRIBUTION_UNIFIED_NETWORK',
                columnSQL: 'user_dim.first_attribution.unified_network',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'user_dim.first_attribution.unified_network',
                        state
                    );
                },
            },
            FIRST_ATTRIBUTION_UNIFIED_CAMPAIGN: {
                columnId: 'FIRST_ATTRIBUTION_UNIFIED_CAMPAIGN',
                columnSQL: 'user_dim.first_attribution.unified_campaign',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'user_dim.first_attribution.unified_campaign',
                        state
                    );
                },
            },
            SEGMENT: {
                columnId: 'SEGMENT',
                columnSQL: 'segments',
                compileSQLTokens: segmentContainerIdCompileSQLTokens,
            },
            SEGMENT_EXCLUDE: {
                columnId: 'SEGMENT_EXCLUDE',
                columnSQL: 'segments',
                compileSQLTokens: segmentContainerIdCompileSQLTokens,
            },
        },
    };

    // compile sql statements specific for this datasource
    public static compileSQLStatements(
        supportedDatasourceColumns: IQueryContextDatasourceColumn[],
        queryContextSettings: IQueryContext['settings'],
        options: { gcpProjectId: GcpProjectId },
        customCompiledSqlTokens?: Partial<ICompiledSQLTokens>
    ) {
        const activityDateColumnSql =
            BiVizDashboardEngagement.sqlMap.columns.ACTIVITY_DATE?.columnSQL!;
        const dateTrunc = queryContextSettings.dateAggregation
            ? sqlDateTrunc(
                  activityDateColumnSql,
                  queryContextSettings.dateAggregation
              )
            : `${activityDateColumnSql}`;

        // if app_version is present in QueryContext
        const is_in_context_app_version =
            supportedDatasourceColumns.findIndex(
                (dsCol) => dsCol.id === 'ACTIVITY_APP_VERSION'
            ) != -1;

        // if segment_container_id is present in splits or filters
        const is_in_context_segment =
            supportedDatasourceColumns.findIndex(
                (dsCol) => dsCol.id === 'SEGMENT'
            ) != -1;

        const sqlTokensArray: Partial<ICompiledSQLTokens>[] =
            supportedDatasourceColumns.map((dsCol) => {
                return this.sqlMap.columns[dsCol.id]!.compileSQLTokens!(
                    dsCol.state,
                    options
                );
            });

        // merge tokens into single deduplicated object of type ICompiledSQLTokens
        const sqlTokens: ICompiledSQLTokens =
            mergeSqlTokensArray(sqlTokensArray);

        // CUSTOM FILTER FOR THIS TABLE
        // sqlTokens.where.push(`validation_flag IS NULL`);

        // unnest app_version_activity if app_version is present in splits or filters
        // TODO to powinno być rozwiązane na poziomie datasource, gdzie compileSQL zwróci atrybut join, z treścią `, UNNEST...`

        const splitSelect: string = buildSplitGroupSQL(sqlTokens.split_group);
        const splitGroupBy = splitSelect ? 'split_group, ' : '';
        const countStarOrDistinctProperId = splitSelect
            ? 'COUNT(DISTINCT proper_id)'
            : 'COUNT(*)';
        const where: string = buildWhereSQL([
            ...sqlTokens.where,
            ...(customCompiledSqlTokens?.where ?? []),
        ]);
        const join: string = sqlTokens.join.join('\n');
        const windowTotal = splitGroupBy ? 'PARTITION BY split_group' : '';
        const windowRunning = splitGroupBy
            ? 'PARTITION BY split_group ORDER BY day_no'
            : 'ORDER BY day_no';

        return {
            dateTrunc,
            splitSelect,
            splitGroupBy,
            is_in_context_segment,
            countStarOrDistinctProperId,
            where,
            windowTotal,
            windowRunning,
            join,
        };
    }
}
